'use client'

import { createTranslator } from 'next-intl'
import { useMemo, useSyncExternalStore } from 'react'

import { useAppLocale } from '@/components/i18n/locale-provider'
import { useInheritedMessages } from '@/components/i18n/messages-provider'
import {
  DEFAULT_LOCALE,
  type Locale,
  localeFromLanguageTag,
} from '@/lib/i18n/config'
import englishAuthRecovery from '@/messages/en/authRecovery.json'

type AuthRecoveryMessages = typeof englishAuthRecovery

/** The catalog loaded for the page's language, once it arrives. */
type LoadedCatalog = { locale: Locale; messages: AuthRecoveryMessages }

let loadedCatalog: LoadedCatalog | null = null
let requestedLocale: Locale | null = null
const listeners = new Set<() => void>()

/**
 * The page's language: the marketing root writes it into `<html lang>`, a
 * product root sets it once the visitor's arrives (`DocumentLanguage`).
 */
function readDocumentLocale(): Locale {
  return localeFromLanguageTag(document.documentElement.lang) ?? DEFAULT_LOCALE
}

/** Starts loading the page language's catalog on the first subscriber. */
function subscribe(onChange: () => void): () => void {
  listeners.add(onChange)
  const locale = readDocumentLocale()
  if (locale !== DEFAULT_LOCALE && requestedLocale !== locale) {
    requestedLocale = locale
    import('@/components/i18n/standalone-catalogs')
      .then((catalogs) => catalogs.loadAuthRecoveryCatalog(locale))
      .then((messages) => {
        loadedCatalog = { locale, messages }
        for (const listener of listeners) {
          listener()
        }
      })
      .catch(() => {
        // English stays: a missing chunk must not break the recovery screen.
      })
  }
  return () => {
    listeners.delete(onChange)
  }
}

function getLoadedCatalog(): LoadedCatalog | null {
  return loadedCatalog
}

function getServerCatalog(): null {
  return null
}

/**
 * The sign-in recovery screen's copy. It renders on every root, above any
 * `ClientMessages` (around the Clerk provider), so it never calls
 * `useTranslations`: it reads an inherited `authRecovery` catalog where one
 * exists, else loads the page language's own (English until it arrives,
 * and when it cannot).
 */
export function useAuthRecoveryCopy() {
  const inherited = useInheritedMessages()?.authRecovery as
    | AuthRecoveryMessages
    | undefined
  const inheritedLocale = useAppLocale()
  const loaded = useSyncExternalStore(
    subscribe,
    getLoadedCatalog,
    getServerCatalog
  )
  return useMemo(() => {
    let locale: Locale = DEFAULT_LOCALE
    let messages = englishAuthRecovery
    if (inherited) {
      locale = inheritedLocale
      messages = inherited
    } else if (loaded) {
      locale = loaded.locale
      messages = loaded.messages
    }
    return createTranslator({
      locale,
      messages: { authRecovery: messages },
      namespace: 'authRecovery',
    })
  }, [inherited, inheritedLocale, loaded])
}
